import { useCallback, useState, ReactElement } from 'react';
import { Button, Card } from '@mui/material';
import { DEFAULT_EC2_ROUTES } from '@localstack/constants';
import { useAwsEffect, useAwsGetter, useRoutes } from '@localstack/services';
import { ButtonWithDialog, Dropdown, PageTitle, SecurityGroupsTable } from '@localstack/ui';

import { NavTabs } from './components/NavTabs';
import { EC2Props } from './types';

export const SecurityGroups = ({ Layout, clientOverrides, routes = DEFAULT_EC2_ROUTES }: EC2Props): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { goto } = useRoutes();
  const {
    data: securityGroups,
    isLoading,
    mutate,
  } = useAwsGetter('EC2', 'describeSecurityGroups', [], { clientOverrides });
  const { deleteSecurityGroup } = useAwsEffect('EC2', ['deleteSecurityGroup'], {
    revalidate: ['describeSecurityGroups'],
    clientOverrides,
  });

  const securityGroupList = securityGroups?.SecurityGroups ?? [];

  const handleDeleteSelected = useCallback(async () => {
    await Promise.all(selectedIds.map((GroupId) => deleteSecurityGroup({ GroupId })));
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="EC2: Security Groups"
      title={<PageTitle title="Security Groups" onMutate={mutate} />}
      tabs={<NavTabs routes={routes} />}
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_EC2_SECURITY_GROUP_CREATE)}>
            Create Security Group
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Security Group(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Security Group(s) will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <SecurityGroupsTable
          securityGroups={securityGroupList}
          loading={isLoading}
          onSelect={setSelectedIds}
          onViewSecurityGroup={({ GroupId }) =>
            GroupId && goto(routes.RESOURCES_EC2_SECURITY_GROUP, { groupId: GroupId })
          }
        />
      </Card>
    </Layout>
  );
};
